import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FeatureFlag } from '@metycle/shared';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useFeatureFlag } from '@/lib/feature-flags';
import {
  useReviewInvoiceMutation,
  type ReviewInvoiceInput,
} from '../api/use-review-invoice-mutation';
import { useInvoiceRenderContext } from '../api/use-invoice-render-context';
import { usePreviewInvoicePdf } from '../api/use-preview-invoice-pdf';
import { useSelectableBanks } from '../api/use-selectable-banks';
import type { InvoiceRow } from '../types';
import { ReviewForm } from './invoice-review-sections';
import { InvoiceReviewPreviewColumn } from './invoice-review-preview-column';
import {
  runPreview,
  submitReview,
  type PreviewMutation,
  type ReviewMutation,
} from './invoice-review-actions';
import {
  EMPTY_EDIT,
  blBodyRendersForKind,
  buildReviewBody,
  hydrateReviewEdit,
  readPersistedOverrides,
  reviewSystemValues,
  type EditState,
  type ReviewSystemValues,
} from './invoice-review-overrides';

export interface InvoiceReviewDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly invoice: InvoiceRow;
  readonly onReviewed?: (() => void) | undefined;
  // When set, the review runs as step 1 of the Issue flow: the primary action
  // saves any pending edits, closes the review, and hands off to the issue
  // confirmation instead of being a standalone save.
  readonly onContinueToIssue?: (() => void) | undefined;
}

// The prefill state. The dialog stays mounted while closed; on each open,
// hydrate once the render-context read settles: every field shows the value
// the document renders today (override if set, else the system resolution),
// so a cancelled edit never resurfaces and the operator confirms real values.
// A failed read degrades to the persisted overrides, never a blocked form.
function useReviewPrefill(open: boolean, invoice: InvoiceRow) {
  const contextQuery = useInvoiceRenderContext(open ? invoice.id : null);
  const [edit, setEdit] = useState<EditState>(EMPTY_EDIT);
  const [hydrated, setHydrated] = useState(false);
  const system = useMemo(() => reviewSystemValues(contextQuery.data), [contextQuery.data]);
  useEffect(() => {
    if (!open) setHydrated(false);
  }, [open]);
  const contextSettled = contextQuery.isSuccess || contextQuery.isError;
  useEffect(() => {
    if (!open || hydrated || !contextSettled) return;
    setEdit(hydrateReviewEdit(invoice, system));
    setHydrated(true);
  }, [open, hydrated, contextSettled, invoice, system]);
  return { edit, setEdit, system, hydrated };
}

// The review write for the current form state against the invoice's persisted
// state — null when there is nothing to save.
function bodyFor(
  invoice: InvoiceRow,
  edit: EditState,
  system: ReviewSystemValues,
): ReviewInvoiceInput | null {
  return buildReviewBody({
    edit,
    blBodyEditable: blBodyRendersForKind(invoice.invoice_type),
    persisted: readPersistedOverrides(invoice.field_overrides),
    system,
    persistedBankId: invoice.issuer_bank_id ?? null,
  });
}

// Holds the rendered preview as an object URL for the inline iframe, revoking
// the prior URL on each new render, when the dialog closes, and on unmount so
// blobs never leak.
function usePreviewObjectUrl(open: boolean) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const previewUrlRef = useRef<string | null>(null);
  const showPreview = useCallback((blob: Blob | null) => {
    if (previewUrlRef.current !== null) URL.revokeObjectURL(previewUrlRef.current);
    const next = blob === null ? null : URL.createObjectURL(blob);
    previewUrlRef.current = next;
    setPreviewUrl(next);
  }, []);
  useEffect(() => {
    if (!open) showPreview(null);
  }, [open, showPreview]);
  useEffect(
    () => () => {
      if (previewUrlRef.current !== null) URL.revokeObjectURL(previewUrlRef.current);
    },
    [],
  );
  return { previewUrl, showPreview };
}

// A non-final sell row can render the running history of its predecessors
// instead of just its own value. The Final always carries the full chain, so the
// toggle is offered only on the other sell kinds, and only when the chain PDF
// feature is on. It defaults on — the chain view is the expected output — and
// resets to that default whenever the dialog closes.
function useChainHistoryToggle(open: boolean, invoice: InvoiceRow) {
  const flagOn = useFeatureFlag(FeatureFlag.INVOICING_CHAIN_COMPOSITE_PDF, false);
  const historyEligible =
    flagOn && invoice.invoice_direction === 'Sale' && invoice.invoice_type !== 'Final_Settlement';
  const [includeChainHistory, setIncludeChainHistory] = useState(true);
  useEffect(() => {
    if (!open) setIncludeChainHistory(true);
  }, [open]);
  return { historyEligible, includeChainHistory, setIncludeChainHistory };
}

// The inline preview: holds the rendered object URL and re-renders on Save &
// preview or when the chain-history toggle flips. Split out so the main review
// hook stays a thin shell.
function useReviewPreview(
  args: Readonly<{
    open: boolean;
    mutation: ReviewMutation;
    previewMutation: PreviewMutation;
    chain: ReturnType<typeof useChainHistoryToggle>;
    buildBody: () => ReviewInvoiceInput | null;
    onSaved: () => void;
  }>,
) {
  const { previewUrl, showPreview } = usePreviewObjectUrl(args.open);
  const previewWith = (history: boolean) => {
    void runPreview({
      body: args.buildBody(),
      save: args.mutation,
      preview: args.previewMutation,
      includeChainHistory: args.chain.historyEligible && history,
      onSaved: args.onSaved,
    }).then((blob) => {
      if (blob !== null) showPreview(blob);
    });
  };
  return {
    previewUrl,
    onPreview: () => {
      previewWith(args.chain.includeChainHistory);
    },
    // Re-render the shown preview immediately on toggle so the pane always matches
    // the checkbox; when nothing is previewed yet the next Save & preview picks it up.
    onToggleChainHistory: (next: boolean) => {
      args.chain.setIncludeChainHistory(next);
      if (previewUrl !== null) previewWith(next);
    },
  };
}

function useBankPicker(open: boolean, invoiceId: string) {
  const on = useFeatureFlag(FeatureFlag.INVOICING_ENTITY_BANK, false);
  const query = useSelectableBanks(on && open ? invoiceId : null);
  return { on, banks: query.data ?? [], isLoading: query.isLoading };
}

// All the review-dialog state + handlers, so the component stays a thin shell.
function useInvoiceReview(props: Readonly<InvoiceReviewDialogProps>) {
  const { open, onOpenChange, invoice, onReviewed, onContinueToIssue } = props;
  const bankPicker = useBankPicker(open, invoice.id);
  const mutation = useReviewInvoiceMutation(invoice.id);
  const previewMutation = usePreviewInvoicePdf(invoice.id);
  const { edit, setEdit, system, hydrated } = useReviewPrefill(open, invoice);
  const chain = useChainHistoryToggle(open, invoice);
  const close = () => {
    onOpenChange(false);
  };
  const onSubmit = () => {
    const finish = () => {
      close();
      onContinueToIssue?.();
    };
    submitReview(mutation, bodyFor(invoice, edit, system), {
      onClose: finish,
      onSaved: () => {
        onReviewed?.();
        finish();
      },
    });
  };
  const preview = useReviewPreview({
    open,
    mutation,
    previewMutation,
    chain,
    buildBody: () => bodyFor(invoice, edit, system),
    onSaved: () => onReviewed?.(),
  });
  return {
    edit,
    setEdit,
    close,
    onSubmit,
    onPreview: preview.onPreview,
    previewUrl: preview.previewUrl,
    system,
    isResolving: !hydrated,
    bankPickerOn: bankPicker.on,
    banks: bankPicker.banks,
    isLoading: bankPicker.isLoading,
    isPending: mutation.isPending,
    isPreviewing: previewMutation.isPending,
    historyEligible: chain.historyEligible,
    includeChainHistory: chain.includeChainHistory,
    onToggleChainHistory: preview.onToggleChainHistory,
  };
}

export function InvoiceReviewDialog(props: Readonly<InvoiceReviewDialogProps>) {
  const { open, onOpenChange, invoice } = props;
  const review = useInvoiceReview(props);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] flex-col overflow-hidden sm:max-w-2xl lg:max-w-5xl">
        <DialogHeader>
          <DialogTitle>Review invoice before issuing</DialogTitle>
          <DialogDescription>
            Contract data is read-only and corrected upstream in Foundry. Only the fields below the
            divider can be edited on the document.
          </DialogDescription>
        </DialogHeader>
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto lg:flex-row lg:overflow-hidden">
          <ReviewForm
            invoice={invoice}
            edit={review.edit}
            setEdit={review.setEdit}
            system={review.system}
            isResolving={review.isResolving}
            bankPickerEnabled={review.bankPickerOn}
            banks={review.banks}
            isLoading={review.isLoading}
            isPending={review.isPending}
            isPreviewing={review.isPreviewing}
            {...(props.onContinueToIssue !== undefined ? { submitLabel: 'Continue' } : {})}
            onSubmit={review.onSubmit}
            onPreview={review.onPreview}
            onCancel={review.close}
          />
          <InvoiceReviewPreviewColumn
            previewUrl={review.previewUrl}
            isPreviewing={review.isPreviewing}
            historyEligible={review.historyEligible}
            includeChainHistory={review.includeChainHistory}
            onToggleChainHistory={review.onToggleChainHistory}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
